import React, { useEffect, useState } from 'react';
import { Navbar, Sidebar, OrderAlertBanner } from './index';
import { useRealtimeNotifications } from '@/hooks/useRealtimeNotifications';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarMinimized, setSidebarMinimized] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { activeAlert, dismissAlert } = useRealtimeNotifications();

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');

    const handleViewportChange = (event: MediaQueryListEvent | MediaQueryList) => {
      if (event.matches) {
        setMobileSidebarOpen(false);
      }
    };

    handleViewportChange(mediaQuery);

    const listener = (event: MediaQueryListEvent) => handleViewportChange(event);
    mediaQuery.addEventListener('change', listener);

    return () => {
      mediaQuery.removeEventListener('change', listener);
    };
  }, []);

  const handleSidebarToggle = () => {
    if (window.innerWidth >= 1024) {
      setSidebarMinimized((current) => !current);
      return;
    }

    setMobileSidebarOpen((current) => !current);
  };

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100 transition-colors relative">
      {/* Top Floating Real-time Order Alert Banner */}
      <OrderAlertBanner
        notification={activeAlert}
        onDismiss={dismissAlert}
      />

      {/* Navbar Header */}
      <Navbar onSidebarToggle={handleSidebarToggle} />

      {/* Body Area */}
      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* Sidebar */}
        <Sidebar
          isMinimized={sidebarMinimized}
          isOpen={mobileSidebarOpen}
          onClose={() => setMobileSidebarOpen(false)}
        />

        {/* Main Content Area */}
        <main className="min-w-0 flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-950">
          <div className="p-3 sm:p-5 lg:p-6 max-w-[1600px] mx-auto min-h-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};
